/**
 * The tune a draft LOOKS like it wants: a lens, a temper, a move budget.
 *
 * `suggestFrame`'s sibling, held to the same contract: A SUGGESTION AND NEVER A
 * SWITCH. The composer offers the reading as a note and a person applies it with
 * a press — through `setLens`, `setTemper` and `setRunTune`, the same store
 * fields the chip rail writes, so afterwards nothing distinguishes a suggested
 * value from a chosen one and the chips themselves show what the press wrote
 * (the D49 shape: data through the ordinary door, no second mechanism).
 *
 * Not in `command.ts`, although that is where `suggestFrame` lives, because that
 * module's charter is the commands — text in, a spec and a clean seed out — and
 * this is a rule table over the PROSE, with thresholds and why-lines that want
 * `cues.ts`'s contract and a test file of their own. It expects the seed
 * `parseCommands` returns, commands already stripped; a `/moves 40` is a command,
 * not a leaning.
 *
 * The rules are few, lexical and ordered, on `leadClaim`'s reasoning: a weighted
 * heuristic would be unreadable and unfalsifiable, and a bad suggestion is worse
 * than none, because the dials already hold a value somebody can see. So each
 * dial fires on a handful of shapes that are almost never anything else, first
 * match wins in a documented order, and everything ordinary stays quiet — a note
 * that fired on every claim would be trained away in a day and take the useful
 * firings with it. The defaults are never suggested: `first-principles`,
 * `balanced` and the middle move row are what "no signal" already means, and a
 * rule that read their absence as their presence would fire on everything.
 *
 * Every cue says WHY — invariant 8's idiom one layer up from the coach: a lens
 * or temper cue quotes the very words it read, verbatim, so the reader can check
 * the rule against their own draft; a moves cue prints the named thresholds its
 * predicate compared. A cue that cannot state why it fired is a verdict handed
 * down, and the composer is the last place to hand one down.
 *
 * A cue is DATA — a dial, a value in the chip rail's own vocabulary, a sentence —
 * never a closure, and the set survives JSON unchanged. Nothing here reads the
 * store, the clock, or a model: the reading spends nothing, which is what lets it
 * run on every keystroke (invariant 9 has nothing to inspect because there is no
 * call to put behind a press).
 */

import type { Lens, Temper } from '../core/types'
import { MOVE_ROWS } from './command'

/** One dial's reading. The values speak the chip rail's own vocabulary, so the
 *  press that applies one writes exactly what the chip's row would have. */
export type TuneCue =
  | { dial: 'lens'; lens: Lens; why: string }
  | { dial: 'temper'; temper: Temper; why: string }
  | { dial: 'moves'; moves: number; why: string }

/** A yes-or-no question at most this many words long reads as one settleable
 *  ask, and gets the smallest move row. Longer ones stay quiet: length is the
 *  only cheap sign that a closed question still opens several directions. */
export const YESNO_MAX_WORDS = 8

/** A draft of at least this many words is a brief, not a claim, and gets the
 *  largest move row — somebody who wrote a paragraph has more than one front
 *  in mind whether or not they named them. */
export const WIDE_MIN_WORDS = 30

/** This many QUESTIONS read as several asks sharing one box. A question is a
 *  run of question marks that ends an ask — trailing whitespace or the end of
 *  the draft after it — so “viable???” is one emphatic ask rather than three,
 *  and the “?” opening a pasted source URL's query string is none at all. */
export const WIDE_MIN_ASKS = 2

/** The rows a moves cue may name — the smallest and largest the app offers,
 *  taken from `MOVE_ROWS`, WHICH THE RADIO ROWS NOW ALSO RENDER FROM (the chip
 *  rail's popover, the tune panel, the defaults panel — they each restated the
 *  triple before this constant was exported). One source, so a change to the
 *  offered rows cannot leave this module suggesting a count no radio button
 *  carries. The middle row is the default and is never suggested. */
export const MOVES_NARROW: number = MOVE_ROWS[0]
export const MOVES_WIDE: number = MOVE_ROWS[MOVE_ROWS.length - 1]!

/** The first capture group when one exists, else the whole match — the words
 *  the why-line quotes back. */
const hit = (re: RegExp, t: string): string | null => {
  const m = re.exec(t)
  return m ? (m[1] ?? m[0]) : null
}

/**
 * The lens rules, IN PRECEDENCE ORDER — first match wins, and the order is a
 * rule a reader can check rather than a score they cannot: an explicit ask for
 * disproof or for precedent says what the person wants DONE and outranks every
 * subject word; a named price outranks an engineering unit because a figure
 * with money on it is an economics question about a technology, not the
 * reverse. `first-principles` never appears: it is the default lens, and "no
 * signal" is not a signal for it.
 *
 * Lexical, although `readsAsClaim` warns against word lists, because the
 * failure modes differ: there a missed verb silently produced nothing, here a
 * missed word leaves the dials exactly where they were. A quiet miss is this
 * feature working as designed; only a wrong firing costs anything.
 */
const LENS_RULES: ReadonlyArray<{ lens: Lens; sign: RegExp; read: (m: string) => string }> = [
  {
    lens: 'adversarial',
    sign: /\b(debunk\w*|disprove|refute[sd]?|myth|hoax|scam|pseudoscien\w+|snake oil|too good to be true)\b/i,
    read: (m) => `“${m}” asks for the fastest route to disproof`,
  },
  {
    lens: 'historical',
    sign: /\b(has (?:anyone|anybody|this|it) (?:ever\s+)?been (?:tried|done|built|attempted)|has (?:anyone|anybody) (?:ever\s+)?(?:tried|done|built|made)|precedent\w*|history of|historical\w*|(?:earlier|previous|prior|past) attempts?|(?:tried|failed) before)\b/i,
    read: (m) => `“${m}” points at precedent and prior attempts`,
  },
  {
    lens: 'economic',
    // The number must END on a digit — `[\d,.]*` alone let “2019,” swallow its
    // clause comma and weld onto the next word, so a draft with no money in it
    // fired this rule quoting nonsense (“2019, dollars”).
    sign: /(\$ ?\d(?:[\d,.]*\d)?|\b\d(?:[\d,.]*\d)? ?(?:dollars|usd)\b|\bunit econom\w+|\bcosts?\b|\bprices?\b|\bmarkets?\b|\bincentives?\b|\bprofit\w*\b|\bcheaper\b|\bsubsidi\w+\b|\beconomic\w*\b)/i,
    read: (m) => `“${m}” makes it a question of cost, incentive and adoption`,
  },
  {
    lens: 'empirical',
    sign: /\b(evidence|measured|measurements?|meta-analys\w+|peer[- ]review\w*|(?:a|the|this|that|any|one) study|studies|trials|null results?|observed|replicat\w+)\b/i,
    read: (m) => `“${m}” asks what has actually been measured`,
  },
  {
    lens: 'systems',
    sign: /\b(feedback loops?|second-order|knock-on|downstream effects?|ripple effects?|supply chains?|ecosystems?|cascad\w+|systemic\w*|network effects?|unintended consequences?)\b/i,
    read: (m) => `“${m}” is about couplings and second-order effects`,
  },
  {
    lens: 'technical',
    // Digit-ending number for the economic rule's reason, and `(?!-)` after the
    // unit because a hyphen supplies a word boundary: without it “2030 km-scale”
    // read as a quantity when the km belonged to the compound beside it.
    sign: /(\b\d(?:[\d,.]*\d)? ?(?:kwh?|mwh?|gwh?|volts?|watts?|amps?|k?hz|[mg]hz|kg|tonnes?|km|nm|psi|bar|[km]pa|kelvin|°[cf])\b(?!-)|\btolerances?\b|\bfailure modes?\b|\bpower budgets?\b|\bcontrol loops?\b|\bthroughput\b|\blatency\b)/i,
    read: (m) => `“${m}” reads like an engineering question of tolerances and failure modes`,
  },
]

/** The reaching shapes: futures and invitations. `balanced` has no rule — see
 *  `suggestTune` for why both tempers matching means silence, not a winner.
 *  “One day” only with a subject after it: bare, it is at least as often a
 *  duration (“a one day fast”), and that fired Reaching on grounded asks. */
const SPECULATIVE_SIGN =
  /\b(what if|imagine|suppose|hypothetical\w*|in principle|(?:one day|some ?day) (?:we|it|they|there)|could (?:we|it|there) ever|will (?:we|it|there) ever|far future|science fiction|sci-fi)\b/i

/** The evidence-first shapes: asks about what is already established. */
const GROUNDED_SIGN =
  /\b(is it (?:actually\s+)?true|actually|in fact|proven|established|the evidence|measured|current bounds|state of the art)\b/i

/** The breadth words: a comparison or an everything-ask opens several fronts
 *  at once, however few words carry it. */
const WIDE_SIGN =
  /\b(compare[sd]?|comparison|versus|vs\.?|trade-?offs?|every (?:way|option|angle|route)|all the (?:ways|options|angles|routes)|exhaustive\w*|comprehensive\w*|end[- ]to[- ]end)\b/i

/** The closed-question openers — the leading word that makes a trailing `?` a
 *  yes-or-no ask rather than an open one. `suggestFrame`'s interrogative list
 *  minus the open words (why, how, what, …), which invite breadth. */
const YESNO_LEAD = /^(is|are|was|were|does|do|did|can|could|will|would|should|has|have|had)\b/i

/**
 * Read a draft for the three dials. Expects the CLEAN seed — what
 * `parseCommands` leaves once the commands are out — and returns nothing rather
 * than something whenever the words are ordinary. The caller drops any cue the
 * dials already satisfy, and the moves cue whenever a typed `/moves` stands: a
 * command is the person saying, and a suggestion that argued with it would be
 * exactly the switch this module refuses to be.
 *
 * At most one cue per dial, in dial order. Two temper signs at once — "what if
 * the evidence is wrong" — cancel to silence, because a draft pulling both ways
 * is what `balanced` MEANS, and balanced is the default no cue may name. The
 * wide moves rules are tried before the narrow one, because a breadth word in a
 * short draft is a wide ask stated briefly, not a narrow one.
 */
export function suggestTune(seed: string): TuneCue[] {
  const t = seed.trim()
  if (!t) return []
  const cues: TuneCue[] = []

  for (const rule of LENS_RULES) {
    const m = hit(rule.sign, t)
    if (m) {
      cues.push({ dial: 'lens', lens: rule.lens, why: rule.read(m) })
      break
    }
  }

  const reach = hit(SPECULATIVE_SIGN, t)
  const ground = hit(GROUNDED_SIGN, t)
  if (reach && !ground) {
    cues.push({
      dial: 'temper',
      temper: 'speculative',
      why: `“${reach}” is an invitation to reach`,
    })
  } else if (ground && !reach) {
    cues.push({
      dial: 'temper',
      temper: 'grounded',
      why: `“${ground}” asks for evidence, not reach`,
    })
  }

  const words = t.split(/\s+/).length
  // Questions, not characters — see `WIDE_MIN_ASKS` for what the lookahead buys.
  const asks = (t.match(/\?+(?=\s|$)/g) ?? []).length
  const wide = hit(WIDE_SIGN, t)
  if (asks >= WIDE_MIN_ASKS) {
    cues.push({
      dial: 'moves',
      moves: MOVES_WIDE,
      why: `${asks} questions in one draft — ${WIDE_MIN_ASKS} or more reads as several asks at once, and ${MOVES_WIDE} moves gives each its own room`,
    })
  } else if (wide) {
    cues.push({
      dial: 'moves',
      moves: MOVES_WIDE,
      why: `“${wide}” opens several fronts at once, and ${MOVES_WIDE} moves gives each its own room`,
    })
  } else if (words >= WIDE_MIN_WORDS) {
    cues.push({
      dial: 'moves',
      moves: MOVES_WIDE,
      why: `${words} words — ${WIDE_MIN_WORDS} or more reads as a brief rather than a claim, and ${MOVES_WIDE} moves gives it room`,
    })
  } else if (words <= YESNO_MAX_WORDS && t.endsWith('?') && YESNO_LEAD.test(t)) {
    cues.push({
      dial: 'moves',
      moves: MOVES_NARROW,
      why: `a yes-or-no question of ${words} words — ${YESNO_MAX_WORDS} or fewer — usually meets its frontier inside ${MOVES_NARROW} moves`,
    })
  }

  return cues
}

/** The dials as they stand when the note would render — plain data, so the
 *  offering rule below stays testable against a literal, the way `parseCommands`
 *  is. `commandMoves` is what a typed `/moves` (or `/within`) put in the spec,
 *  when one did. */
export interface TuneDials {
  lens: Lens
  temper: Temper
  moves: number
  commandMoves: number | undefined
}

/**
 * Which of a reading's cues the composer may OFFER, against the dials in force.
 *
 * Half of the never-a-switch contract lives here rather than in the note's JSX,
 * so it is pinned by tests instead of by hope: a cue the dial already satisfies
 * says nothing (there is nothing to offer), and the moves cue stands down
 * ENTIRELY under a typed moves command — agreeing or not — because a command is
 * the person saying, and a suggestion that argued with it would be a switch
 * wearing a note's clothes.
 */
export function offeredTune(cues: TuneCue[], dials: TuneDials): TuneCue[] {
  return cues.filter((c) =>
    c.dial === 'lens'
      ? c.lens !== dials.lens
      : c.dial === 'temper'
        ? c.temper !== dials.temper
        : dials.commandMoves === undefined && c.moves !== dials.moves,
  )
}
